/**
 * Cart Drawer Upsells Module
 * Handles add-to-cart functionality and cart drawer refresh
 */
(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    container: '[data-upsells-container]',
    item: '[data-upsell-item]',
    addBtn: '[data-upsell-add-btn]',
    variantSelect: '[data-upsell-variant-select]',
    priceDisplay: '[data-upsell-price]'
  };

  // State to prevent rapid clicks
  let isProcessing = false;

  /**
   * Initialize upsells module
   */
  function init() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    // Event delegation for add buttons
    container.addEventListener('click', handleAddClick);

    // Event delegation for variant select changes
    container.addEventListener('change', handleVariantChange);
  }

  /**
   * Handle add to cart button clicks
   */
  async function handleAddClick(event) {
    const button = event.target.closest(SELECTORS.addBtn);
    if (!button || isProcessing) return;

    event.preventDefault();

    // Get variant ID (from button data or select)
    const item = button.closest(SELECTORS.item);
    const select = item.querySelector(SELECTORS.variantSelect);
    const variantId = select ? parseInt(select.value, 10) : parseInt(button.dataset.variantId, 10);

    if (!variantId) {
      console.error('[Upsells] No variant ID found');
      return;
    }

    // Set loading state
    isProcessing = true;
    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || `HTTP ${response.status}`);
      }

      // Success - refresh cart drawer UI
      await refreshCartDrawer();

      // Notify any listeners (apps, price-per-item logic, etc.) that the cart
      // has changed, mirroring the behaviour of product-form.js and cart.js.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsells',
            productVariantId: variantId,
            cartData: data,
          });
        } catch (pubError) {
          console.warn('[Upsells] cartUpdate publish failed:', pubError);
        }
      }

    } catch (error) {
      console.error('[Upsells] Add to cart failed:', error);

      // Show error notification if available
      if (typeof window.showCartNotification === 'function') {
        window.showCartNotification(error.message, 'error');
      } else {
        alert('Failed to add to cart. Please try again.');
      }

      // Reset button state on error
      button.classList.remove('is-loading');
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');

    } finally {
      isProcessing = false;
    }
  }

  /**
   * Handle variant select change - update price display
   */
  function handleVariantChange(event) {
    if (!event.target.matches(SELECTORS.variantSelect)) return;

    const select = event.target;
    const item = select.closest(SELECTORS.item);
    const priceDisplay = item.querySelector(SELECTORS.priceDisplay);
    const addBtn = item.querySelector(SELECTORS.addBtn);
    const selectedOption = select.options[select.selectedIndex];

    if (priceDisplay && selectedOption.dataset.price) {
      priceDisplay.textContent = selectedOption.dataset.price;
      
      // Update compare price if exists
      const priceWrapper = priceDisplay.closest('.cart-drawer-upsell-item__price-wrapper');
      const comparePrice = priceWrapper.querySelector('.cart-drawer-upsell-item__compare-price');
      
      if (selectedOption.dataset.comparePrice && selectedOption.dataset.comparePrice !== '0') {
        if (!comparePrice) {
          const comparePriceEl = document.createElement('span');
          comparePriceEl.className = 'cart-drawer-upsell-item__compare-price';
          priceWrapper.insertBefore(comparePriceEl, priceDisplay);
        }
        priceWrapper.querySelector('.cart-drawer-upsell-item__compare-price').textContent = selectedOption.dataset.comparePrice;
      } else if (comparePrice) {
        comparePrice.remove();
      }
    }

    if (addBtn) {
      addBtn.dataset.variantId = select.value;
    }
  }

  /**
   * Refresh the cart drawer after adding item
   * 
   * Uses the theme's existing cart refresh mechanism:
   * - Finds cart-drawer-items component
   * - Calls onCartUpdate() method which fetches /cart?section_id=cart-drawer
   * - Replaces specific selectors including cart-drawer-items
   */
  async function refreshCartDrawer() {
    // Method 1: Use cart-drawer-items onCartUpdate() method (primary method)
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      try {
        await cartDrawerItems.onCartUpdate();
        return;
      } catch (e) {
        console.warn('[Upsells] onCartUpdate failed, trying fallback:', e);
      }
    }

    // Method 2: Direct fetch and replace (fallback).
    try {
      const response = await fetch('/cart?section_id=cart-drawer');
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Replace the entire drawer inner content from the refreshed section,
      // mirroring the behavior of CartItems.onCartUpdate for cart-drawer-items.
      const cartDrawer = document.querySelector('cart-drawer');
      const newDrawerInner = doc.querySelector('#CartDrawer .drawer__inner');
      const currentDrawerInner = cartDrawer?.querySelector('.drawer__inner');

      if (cartDrawer && newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      // Update empty state based on the refreshed cart-drawer-items
      const updatedCartDrawerItems = cartDrawer?.querySelector('cart-drawer-items');
      const headerBubbleIcon = document.querySelector('header .cart-count-bubble');

      if (updatedCartDrawerItems?.hasAttribute('data-real-items-count')) {
        let itemCount = +updatedCartDrawerItems.dataset.realItemsCount;
        cartDrawer?.classList.toggle('is-empty', itemCount === 0);
        headerBubbleIcon?.classList.toggle('is-empty', itemCount === 0);
      }

    } catch (e) {
      console.error('[Upsells] Cart refresh failed:', e);
    }
  }

  // Initialize on DOM ready and on cart drawer open
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when cart drawer content is replaced
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        const hasUpsells = document.querySelector(SELECTORS.container);
        if (hasUpsells) {
          init();
          break;
        }
      }
    }
  });

  // Observe cart drawer for content changes
  const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, [data-cart-drawer], .cart-drawer');
  if (cartDrawer) {
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

})();

